import { SolicitudVacaciones, EstatusVacaciones } from '@schemas/vacaciones.schema';

/**
 * Colores de tag/badge por estatus.
 */
export const ESTATUS_COLORES: Record<EstatusVacaciones, string> = {
  Pendiente: 'orange',
  'Aprobada Director': 'blue',
  Aprobada: 'green',
  'Rechazada Director': 'red',
  Rechazada: 'red',
  Cancelada: 'default',
};

/**
 * Clases de badge por estatus (para nz-badge custom).
 */
export const ESTATUS_BADGE_CLASSES: Record<EstatusVacaciones, string> = {
  Pendiente: 'badge-pendiente',
  'Aprobada Director': 'badge-aprobada-dir',
  Aprobada: 'badge-activo',
  'Rechazada Director': 'badge-rechazada',
  Rechazada: 'badge-inactivo',
  Cancelada: 'badge-cancelada',
};

/**
 * Tono semántico del estatus, para el estado en línea (`.estado-inline`).
 *
 * Independiente de `ESTATUS_COLORES`: aquel nombra colores de `nz-tag` (la
 * paleta de ng-zorro) y sirve para las tablas; esto agrupa los seis estatus en
 * las cinco lecturas que necesita la UI —espera, avance, cierre bueno, cierre
 * malo y baja— sin atarse a una librería.
 */
export type TonoEstatus = 'pendiente' | 'progreso' | 'ok' | 'error' | 'neutro';

export const ESTATUS_TONO: Record<EstatusVacaciones, TonoEstatus> = {
  Pendiente: 'pendiente',
  'Aprobada Director': 'progreso',
  Aprobada: 'ok',
  'Rechazada Director': 'error',
  Rechazada: 'error',
  Cancelada: 'neutro',
};

/** Tono de lectura del estatus, para `<intelishs-status-text [tone]>`. */
export function getEstatusTono(estatus: EstatusVacaciones): TonoEstatus {
  return ESTATUS_TONO[estatus] ?? 'neutro';
}

/**
 * Devuelve el color de un tag para el estatus dado.
 */
export function getEstatusColor(estatus: EstatusVacaciones): string {
  return ESTATUS_COLORES[estatus] ?? 'default';
}

/**
 * Devuelve la clase CSS de badge para el estatus dado.
 */
export function getEstatusBadgeClass(estatus: EstatusVacaciones): string {
  return ESTATUS_BADGE_CLASSES[estatus] ?? 'badge-pendiente';
}

/**
 * Texto descriptivo del resultado final de una solicitud.
 */
export function getResultadoTexto(sol: SolicitudVacaciones): string {
  switch (sol.estatus) {
    case 'Aprobada':
      return 'Solicitud aprobada por RH';
    case 'Rechazada':
      return 'Solicitud rechazada por RH';
    case 'Rechazada Director':
      return 'Solicitud rechazada por Director';
    case 'Cancelada':
      return 'Solicitud cancelada';
    default:
      return '';
  }
}

/**
 * Tipo de resultado para estilos del banner (success | danger | warning | '').
 */
export function getResultadoTipo(sol: SolicitudVacaciones): string {
  if (sol.estatus === 'Aprobada') return 'success';
  if (sol.estatus === 'Rechazada' || sol.estatus === 'Rechazada Director') return 'danger';
  if (sol.estatus === 'Cancelada') return 'warning';
  return '';
}

/**
 * Motivo del rechazo (comentario público del rechazo).
 */
export function getResultadoMotivo(sol: SolicitudVacaciones): string {
  if (sol.estatus === 'Rechazada' && sol.comentariosRH && sol.aprobadoPorRH !== 'Auto-aprobado')
    return sol.comentariosRH;
  if (
    sol.estatus === 'Rechazada Director' &&
    sol.comentariosDirector &&
    sol.aprobadoPorDirector !== 'Auto-aprobado'
  )
    return sol.comentariosDirector;
  return '';
}

/**
 * Devuelve true si la solicitud NO cumple la política de 2 semanas de anticipación.
 * Compara la fecha en que se hizo la solicitud con la fecha de inicio de vacaciones.
 * Si la diferencia es menor a 14 días naturales, se considera fuera de política.
 *
 * Tolera ambos formatos del backend:
 *   • `"2026-05-04"` (mocks viejos, fecha plana)
 *   • `"2026-05-04T17:30:25.123Z"` (timestamp ISO real desde
 *     `MovEmpleadoVacacion.FechaSolicitud = UtcNow`).
 */
export function incumplePoliticaAnticipacion(fechaSolicitud: string, fechaInicio: string): boolean {
  const solicitudDate = new Date(fechaSolicitud);
  const inicioDate = new Date(fechaInicio);
  if (isNaN(solicitudDate.getTime()) || isNaN(inicioDate.getTime())) return false;
  const diffDias = (inicioDate.getTime() - solicitudDate.getTime()) / (1000 * 60 * 60 * 24);
  return diffDias < 14;
}

/**
 * Días de ESTA solicitud que el saldo ya trae descontados, o 0 si no cuentan.
 *
 * El backend considera consumidos los días de las solicitudes aprobadas, las
 * aprobadas por el director y **las pendientes** —lo pendiente se reserva para
 * que dos solicitudes simultáneas no gasten el mismo día dos veces—; las
 * rechazadas y las canceladas devuelven sus días.
 *
 * Existe porque el saldo que muestran los modales YA viene neto y eso no se
 * decía en ninguna parte: con 24.4 disponibles y una solicitud de 11 días, el
 * detalle mostraba "Total disponible 13.4 días" a secas. Quien autoriza leía
 * 13.4 como el saldo actual del empleado y decidía sobre un número que en
 * realidad era la proyección de la propia solicitud que estaba revisando. El
 * modal de aprobación sí lo desglosa (`Disponibles hoy` − `Esta solicitud` =
 * `Le queda tras aprobar`); los demás no lo mencionaban.
 */
export function diasDescontadosDelSaldo(sol: SolicitudVacaciones | null | undefined): number {
  if (!sol) return 0;
  const cuenta =
    sol.estatus === 'Pendiente' ||
    sol.estatus === 'Aprobada Director' ||
    sol.estatus === 'Aprobada';
  return cuenta ? sol.diasSolicitados : 0;
}

/**
 * Lista completa de estatus válidos.
 */
export const ESTATUS_OPCIONES: EstatusVacaciones[] = [
  'Pendiente',
  'Aprobada Director',
  'Aprobada',
  'Rechazada Director',
  'Rechazada',
  'Cancelada',
];

export type EstadoPaso = 'hecho' | 'actual' | 'pendiente' | 'rechazado' | 'cancelado';

export interface PasoFlujo {
  titulo: string;
  estado: EstadoPaso;
  /** Timestamp del paso, si ya ocurrió. */
  fecha?: string;
  /**
   * Texto en segunda persona para el empleado ("Tu director la está
   * revisando"). Solo lo pinta su rastreador: al director y a RH no les
   * acompaña, les informa.
   */
  detalle?: string;
  /** Quién resolvió. Solo para el registro de RH/director. */
  nombre?: string;
  /** Lo que escribió al resolver. Solo para el registro de RH/director. */
  comentario?: string;
}

/**
 * Los pasos del flujo de una solicitud, con el estado de cada uno.
 *
 * Fuente ÚNICA para los dos modales que cuentan la misma historia: el
 * rastreador del empleado (`solicitud-resolucion-modal`) y el registro de RH y
 * del director (`solicitud-detalle-modal`). Cada uno decide qué campos pinta
 * —el empleado ve `detalle` y nunca nombres ni comentarios; RH ve `nombre` y
 * `comentario` y no necesita el texto de acompañamiento—, pero la secuencia y
 * los estados salen de aquí.
 *
 * Estuvo duplicada y por eso se unifica: dos máquinas de estados para el mismo
 * proceso divergen a la primera regla nueva, igual que divergieron las dos
 * vistas del saldo leyendo campos distintos para "disponibles".
 *
 * La regla menos obvia: al rechazar o cancelar, **el paso que falta no se
 * dibuja**. En gris sugeriría que sigue en espera, cuando ya no va a ocurrir.
 */
export function getPasosFlujo(sol: SolicitudVacaciones): PasoFlujo[] {
  const enviada: PasoFlujo = {
    titulo: 'Solicitud enviada',
    fecha: sol.fechaSolicitud,
    estado: 'hecho',
  };

  const autor = (nombre?: string | null): string =>
    !nombre || nombre === 'Auto-aprobado' ? '' : nombre;

  /**
   * Nombre, fecha y comentario solo cuando el paso YA se resolvió. Sin esta
   * guarda, un paso en curso mostraba el nombre de quien lo tiene asignado
   * —"Revisión del director · Roberto"— como si ya hubiera resuelto.
   */
  const resuelto = (estado: EstadoPaso): boolean => estado === 'hecho' || estado === 'rechazado';

  const director = (estado: EstadoPaso, detalle?: string): PasoFlujo => ({
    titulo: 'Revisión del director',
    detalle,
    fecha: resuelto(estado) ? (sol.fechaAprobacionDirector ?? undefined) : undefined,
    nombre: resuelto(estado) ? autor(sol.aprobadoPorDirector) : '',
    comentario:
      resuelto(estado) && sol.comentariosDirector !== 'Auto-aprobado (DG)'
        ? (sol.comentariosDirector ?? '')
        : '',
    estado,
  });

  const rh = (estado: EstadoPaso, detalle?: string): PasoFlujo => ({
    titulo: 'Validación de RH',
    detalle,
    fecha: resuelto(estado) ? (sol.fechaAprobacionRH ?? undefined) : undefined,
    nombre: resuelto(estado) ? autor(sol.aprobadoPorRH) : '',
    comentario:
      resuelto(estado) && sol.comentariosRH !== 'Auto-aprobado (DG)'
        ? (sol.comentariosRH ?? '')
        : '',
    estado,
  });

  // RH puede resolver una Pendiente sin esperar al jefe. Entonces su paso queda
  // sin fecha —la señal; el nombre no sirve, porque se pre-asigna al crear— y
  // se dibuja como omitido en vez de "Aprobada por tu director", que sería
  // falso. Ver `CambiarEstatusRHAsync` en recursoshumanosapi.
  const sinJefe = !sol.fechaAprobacionDirector;
  const directorOmitido: PasoFlujo = {
    titulo: 'Revisión del director',
    detalle: 'RH resolvió sin esperar a tu director.',
    nombre: 'Resuelta directamente por RH',
    estado: 'cancelado',
  };

  switch (sol.estatus) {
    case 'Pendiente':
      return [
        enviada,
        director('actual', 'Tu director la está revisando.'),
        rh('pendiente', 'Se validará cuando tu director la apruebe.'),
      ];

    case 'Aprobada Director':
      return [
        enviada,
        director('hecho', 'Aprobada por tu director.'),
        rh('actual', 'RH la revisará próximamente.'),
      ];

    case 'Aprobada':
      return [
        enviada,
        sinJefe ? directorOmitido : director('hecho', 'Aprobada por tu director.'),
        rh('hecho', 'Tus vacaciones quedaron confirmadas.'),
      ];

    case 'Rechazada Director':
      return [enviada, director('rechazado', 'Tu director no aprobó la solicitud.')];

    case 'Rechazada':
      return [
        enviada,
        sinJefe ? directorOmitido : director('hecho', 'Aprobada por tu director.'),
        rh('rechazado', 'RH no aprobó la solicitud.'),
      ];

    case 'Cancelada':
      return [enviada, director('cancelado', 'La solicitud fue cancelada.')];

    default:
      return [enviada];
  }
}
